import { formatWithTimeZone, isStartOfDayInTimeZone, ONE_DAY, ONE_MINUTE, TimeFormat } from '@chronosphereio/core';
import { DAY_MS, MONTH_MS, YEAR_MS } from '@/core';

export type getFormattedDateProps = {
  /**
   * The timestamp value (in milliseconds) to format.
   */
  value: number;
  /**
   * The total time range (in milliseconds) represented by the chart,
   * used to determine the granularity of the formatted output.
   */
  rangeMs: number;
  timeFormat: TimeFormat | undefined;
  timeZone?: string;
};

/*
 * Determines time granularity for events histogram x axis labels
 */
export function getFormattedDate({ value, rangeMs, timeFormat, timeZone }: getFormattedDateProps) {
  const date = new Date(value);

  let format = 'HH:mm';
  // less than or equal to 30 minutes
  if (rangeMs <= ONE_MINUTE * 30) {
    format = 'HH:mm:ss';
  }
  // greater than or equal to one day
  else if (rangeMs >= ONE_DAY) {
    format = 'MMM dd HH:mm';
  }

  format = adjustFormatForTimePreference(format, timeFormat);
  return formatWithTimeZone(date, format, timeZone);
}

export interface legacyGetFormattedDateArgs extends Omit<getFormattedDateProps, 'value'> {
  label: string;
}

/*
 * Determines time granularity for cateogry axis labels, used in deprecated LineChartBase
 */
export function legacyGetFormattedDate({ label, rangeMs, timeFormat, timeZone }: legacyGetFormattedDateArgs) {
  // Category axis passes timestamp as string so convert to number
  const date = new Date(Number(label));

  let format: string;
  if (rangeMs > YEAR_MS * 5) {
    // more than 5 years
    format = 'yyyy';
  } else if (rangeMs > YEAR_MS * 2) {
    // more than 2 years
    format = 'MMM yyyy';
  } else if (rangeMs > DAY_MS) {
    // more than one day
    format = 'MMM dd HH:mm';
  } else {
    // less than one day
    format = 'HH:mm';
  }

  format = adjustFormatForTimePreference(format, timeFormat);
  return formatWithTimeZone(date, format, timeZone);
}

/*
 * Gets formatted x axis label, used in heatmaps
 * https://echarts.apache.org/en/option.html#xAxis.axisLabel.formatter
 */
export function getFormattedAxisLabel(rangeMs: number) {
  // more than 5 years
  if (rangeMs > YEAR_MS * 5) {
    return '{yyyy}';
  }

  // more than 2 years
  if (rangeMs > YEAR_MS * 2) {
    return '{MMM} {yyyy}';
  }

  // between 5 days to 6 months
  if (rangeMs > DAY_MS * 5 && rangeMs < MONTH_MS * 6) {
    return '{MMM} {dd}'; // Dec 01
  }

  // between 2 and 5 days
  if (rangeMs > DAY_MS * 2 && rangeMs <= DAY_MS * 5) {
    return '{MMM} {dd} {HH}:{mm}'; // Dec 01 12:30
  }

  // less than 2 days
  return {
    year: '{yearStyle|{yyyy}}\n{monthStyle|{MMM}}',
    month: '{MMM}', // Jan, Feb, ...
    day: '{MMM} {dd}',
  };
}

/**
 * Custom time axis label formatter to control format based on active time range and user's preferred time zone
 * Workaround is using similar approach as: https://github.com/apache/echarts/issues/20027#issuecomment-2165555578
 * Used in TimeSeriesChart
 */
export function getFormattedTimeAxisLabel({ value, rangeMs, timeFormat, timeZone }: getFormattedDateProps): string {
  const date: Date = new Date(value);

  // Determine the appropriate format based on the time range
  let format: string;

  if (rangeMs > YEAR_MS * 5) {
    // more than 5 years
    format = 'yyyy';
  } else if (rangeMs > YEAR_MS * 2) {
    // more than 2 years
    format = 'MMM yyyy';
  } else if (rangeMs > DAY_MS * 5) {
    // between 5 days to 2 years
    format = 'MMM dd';
  } else if (rangeMs > DAY_MS * 2) {
    // between 2 and 5 days
    format = 'MMM dd HH:mm';
  } else if (rangeMs > 0) {
    // up to 2 days
    if (isStartOfDayInTimeZone(date, timeZone)) {
      format = 'MMM dd';
    } else {
      format = 'HH:mm';
    }
  } else {
    // Fallback, unlikely to be used
    format = 'HH:mm:ss';
  }

  format = adjustFormatForTimePreference(format, timeFormat);
  return formatWithTimeZone(date, format, timeZone);
}

const HOUR_FORMAT_REGEX = /\bHH\b/;

/*
 * Adjusts the format string based on the user's preference for 12 or 24 hour clock
 */
export function adjustFormatForTimePreference(formatString: string, timeFormat: TimeFormat | undefined) {
  if (timeFormat === '12h' && formatString.includes('HH')) {
    // convert 24h to 12h format
    return formatString.replace(HOUR_FORMAT_REGEX, 'hh') + ' a';
  }
  return formatString;
}
